import { Col, Modal, Row, Skeleton } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { projectTypeOptionsFor } from '../../../common/constants';
import useServiceTicketsEnabled from '../../../hooks/useServiceTicketsEnabled';
import VSpace from '../../common/VSpace';
import { DatacenterReportTypes, DateRanges, getDatacenterReportTypeNames, getFiltersFromJson, ReportFilterViewModel, updateFilterDates } from '../datacenterModel';
import { useCreateFiltersMutation, useDeleteFiltersMutation, useGetFiltersQuery } from '../datacenterQueries';
import ReportFilters from '../ReportFilters';
import ReportSavedFiltersList from '../ReportSavedFiltersList';
import ReportStatistics from '../ReportStatistics';
import SaveFilterModal from '../SaveFilterModal';
import ProposalReportFilterDetails from './ProposalReportFilterDetails';
import { ProposalsSummary } from './proposalsReportApi';
import ProposalsReportJobList from './ProposalsReportJobList';
import { getFilteredProposalsData, getFilteredDetailedCloseData, getFilteredDetailedTurnaroundData, getProposalStatistics, getUniqueDivisionNames, getUniqueSalesPersonNames, addApprovedToSent } from './proposalsReportModel';
import { getProposalExportRows, noProposals } from './proposalsReportExport';
import useDashboardReporting from '../../../hooks/useDashboardReporting';
import { useProposalsReportDataQuery } from './proposalsReportQueries';

export type ProposalsReportFilters = {
  startDate: Dayjs;
  endDate: Dayjs;
  salesPersonIds: string[];
  projectTypeIds: number[];
  dateRange: DateRanges;
  divisionIds: string[];
}

const ProposalsReport = () => {
  const showServiceTickets = useServiceTicketsEnabled()
  const dashboardReporting = useDashboardReporting()
  const projectTypeOptions = projectTypeOptionsFor(showServiceTickets)
  const today = dayjs();
  const defaultStartDate = today.subtract(6, 'month');
  const defaultFilters: ProposalsReportFilters = {
    startDate: defaultStartDate,
    endDate: today,
    salesPersonIds: [],
    projectTypeIds: [],
    dateRange: 'custom',
    divisionIds: [],
  }
  const [open, setOpen] = useState(false);
  const [deleteFilterModalConfirm, deleteFiltersModalContext] = Modal.useModal();
  const [filterName, setFilterName] = useState('');
  const [filters, setFilters] = useState<ProposalsReportFilters>(defaultFilters)
  const { startDate, endDate, salesPersonIds, projectTypeIds, divisionIds } = filters;

  const proposalsReportQuery = useProposalsReportDataQuery(
    startDate.toISOString().split('T')[0],
    endDate.toISOString().split('T')[0]
  );
  const getFiltersQuery = useGetFiltersQuery(DatacenterReportTypes.Proposal)
  const createFiltersMutation = useCreateFiltersMutation()
  const deleteFiltersMutation = useDeleteFiltersMutation()

  const proposalsData: ProposalsSummary = addApprovedToSent(proposalsReportQuery.data?.groupedProposalsData || {
    unsent: [],
    sent: [],
    approved: [],
    lost: [],
    pending: []
  });
  const filtersList: ReportFilterViewModel[] = getFiltersQuery.data?.map((x) => {
    return {
      ...x,
      filters: getFiltersFromJson(x.filters)
    }
  }).sort((a, b) => a.order - b.order) || [];

  const filteredProposalsData = useMemo<ProposalsSummary>(
    () => getFilteredProposalsData(proposalsData, salesPersonIds, projectTypeIds, divisionIds),
    [proposalsData, salesPersonIds, projectTypeIds, divisionIds]
  );

  const salesPersonNames = useMemo<string[]>(
    () => proposalsData ? getUniqueSalesPersonNames(proposalsData) : [],
    [proposalsData]
  );

  const divisionOptions = useMemo<string[]>(
    () => proposalsData? getUniqueDivisionNames(proposalsData) : [],
    [proposalsData]
  );

  const onSaveFilter = () => {
    createFiltersMutation.mutate({
      request: {
        reportId: DatacenterReportTypes.Proposal,
        name: filterName,
        reportTypeName: getDatacenterReportTypeNames(DatacenterReportTypes.Proposal),
        filters: updateFilterDates(filters)
      },
      reportType: DatacenterReportTypes.Proposal
    });
    setOpen(false);
    setFilterName('');
  }

  if (proposalsReportQuery.isLoading) {
    return <Skeleton active />;
  }

  return (<Row gutter={16}>
    {
      (filtersList.length > 0) && <Col span={4}>
        <ReportSavedFiltersList
          filtersList={filtersList}
          onFilterSelect={(filters) => setFilters(filters as ProposalsReportFilters)}
          onDeleteFilters={(selectedFiltersIds) => {
            deleteFilterModalConfirm.confirm({
              title: 'Do you want to delete the selected filters?',
              content: 'This action cannot be undone',
              onOk: () => {
                deleteFiltersMutation.mutate({ filtersIds: selectedFiltersIds, reportType: DatacenterReportTypes.Proposal })
              },
              okText: 'Delete',
              okButtonProps: {
                loading: deleteFiltersMutation.isLoading,
                danger: true
              }
            })
          }}
          renderFilterDetails={(item) => <ProposalReportFilterDetails filterItem={item} />}
          reportType={DatacenterReportTypes.Proposal}
        />
      </Col>
    }
    <Col span={(filtersList.length > 0) ? 20 : 24}>
      <VSpace>
        <ReportFilters
          filters={filters}
          onFiltersChange={setFilters}
          isDataLoading={proposalsReportQuery.isLoading}
          defaultFilters={defaultFilters}
          onSaveFilters={() => {
            setOpen(true)
          }}
          selectOptions={{
            salesPersonIds: {
              mode: 'multiple',
              options: salesPersonNames.map((name) => ({
                label: name,
                value: name,
              })),
              placeholder: 'Select Sales Persons',
              style: { width: 300 },
            },
            projectTypeIds: {
              mode: 'multiple',
              options: projectTypeOptions,
              placeholder: 'Select Project Types',
              style: { width: 300 },
            },
            divisionIds: {
              mode: 'multiple',
              options: divisionOptions.map((name) => ({
                label: name,
                value: name,
              })),
              placeholder: 'Select Divisions',
              style: { width: 300 },
            },
          }}
        />
        <ReportStatistics
          title={
            <span style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Summary</span>
            </span>
          }
          statistics={getProposalStatistics(filteredProposalsData, getFilteredDetailedCloseData(proposalsReportQuery.data?.detailedDaysToCloseData || [], salesPersonIds, projectTypeIds, divisionIds ?? []), getFilteredDetailedTurnaroundData(proposalsReportQuery.data?.detailedTurnaroundData || [], salesPersonIds, projectTypeIds, divisionIds ?? []))}
        />
        <ProposalsReportJobList
          proposals={filteredProposalsData}
          exportRows={dashboardReporting ? getProposalExportRows(proposalsReportQuery.data?.groupedProposalsData ?? noProposals, salesPersonIds, projectTypeIds, divisionIds) : []}
          dateRange={{ start: startDate, end: endDate }}
        />
      </VSpace>
    </Col>

    <SaveFilterModal
      inputValue={filterName}
      onCancel={() => {
        setFilterName('');
        setOpen(false);
      }}
      onInputChange={setFilterName}
      onOk={onSaveFilter}
      open={open}
      title='Save the Filter'
      okText='Save Filter'
      cancelText='Cancel'
    />
    {deleteFiltersModalContext}
  </Row>
  );
};

export default ProposalsReport;